import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import {
  getArchitectureTree,
  getProfilesByUlids,
  fetchJobFamilies,
  fetchJobFamilyGroups,
  bulkUpdateProfileFamily,
  bulkRemoveProfilesFromFamily,
  createJobFamilyGroup,
  createJobFamily,
  updateJobFamilyGroupName,
  updateJobFamilyName,
  updateJobFamilyGroup,
  deleteJobFamily,
  fetchJobFamilyDraftCount,
} from '@/api/architecture';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppAuthData } from '@/stores/app/authData';
import { format } from 'date-fns';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import { useTracking } from '@/stores/tracking/tracking';
import {
  architectureDisplayName,
  BULK_ARCHITECTURE_ACTION_LIMIT,
  countBulkActionSuccesses,
  getGroupArchitectureStats,
  getGroupTotalProfileCount,
  getFamilyArchitectureStats,
  getFamiliesForProfiles,
  getPublishedArchitectureProfiles,
} from '@/components/jobArchitecture/helpers';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';
import { normalizeCustomFields } from './helpers';

export const useJobArchitecture = defineStore('jobArchitecture', () => {
  const { setLoading } = useAppLoading();
  const snackbar = useAppSnackbar();
  const { isStandaloneJobNav } = storeToRefs(useAppAuthData());

  const treeData = ref(null);
  const selectedFamily = ref(null);
  const selectedFamilyProfiles = ref([]);

  // Set of expanded group node IDs
  const expandedGroupIds = ref(new Set());

  const xlsxDownloadLoading = ref(false);

  const selectedProfileUlids = ref([]);

  const isEditMode = ref(false);
  const editActionLoading = ref(false);
  const jobFamilies = ref([]);
  const jobFamilyGroups = ref([]);
  const rowAction = ref(null);

  /** Saved list filter/search state when navigating to compare; cleared after restore on return. */
  const listFilterSnapshot = ref(null);

  /** Group/family tree node ids to expand when returning from compare (list view). */
  const listDefaultExpandedNodeIds = ref(null);

  const clearListDefaultExpandedNodeIds = () => {
    listDefaultExpandedNodeIds.value = null;
  };

  const setListDefaultExpandedNodeIds = (ids) => {
    listDefaultExpandedNodeIds.value = ids ?? null;
  };

  const setSelectedProfileUlids = (ulids) => {
    const next = Array.isArray(ulids) ? [...ulids] : [];

    selectedProfileUlids.value = next;

    useTracking().jaListSelectedProfiles({ count: next.length });

    if (next.length === 0) {
      clearListDefaultExpandedNodeIds();
    }
  };

  const clearSelectedProfileUlids = () => {
    setSelectedProfileUlids([]);
  };

  const closeRowAction = () => {
    rowAction.value = null;
  };

  const resetEditMode = () => {
    isEditMode.value = false;

    closeRowAction();
  };

  const openRowAction = async (type, node) => {
    if (type === 'move-family' || type === 'delete-family') {
      const draftCountFromTree = Number.isFinite(node.draftCount) ? node.draftCount : null;

      if (isStandaloneJobNav.value && draftCountFromTree !== null) {
        rowAction.value = { ...node, type, draftCount: draftCountFromTree };

        return;
      }

      try {
        const { count } = await fetchJobFamilyDraftCount({ ulid: node.ulid });

        rowAction.value = { ...node, type, draftCount: count };
      } catch (err) {
        snackbar.addErrorSnackbar({
          text: `Failed to load job family — ${err.message || err}`,
        });

        throw err;
      }

      return;
    }

    rowAction.value = { ...node, type, draftCount: 0 };
  };

  const setCompareSelectionUlids = (ulids) => {
    setSelectedProfileUlids(ulids);
  };

  const saveListFilterSnapshot = ({ fields, searchText }) => {
    listFilterSnapshot.value = {
      fields: fields.map((field) => ({
        id: field.id,
        value: field.value.map(({ id, label }) => ({ id, label })),
      })),
      searchText: searchText || '',
    };
  };

  const getAndClearListFilterSnapshot = () => {
    const snapshot = listFilterSnapshot.value;

    listFilterSnapshot.value = null;

    return snapshot;
  };

  const clearListFilterSnapshot = () => {
    listFilterSnapshot.value = null;
  };

  const loadTree = async () => {
    setLoading({ pageLoading: true });

    try {
      // Drafts only show while editing in standalone.
      const response = await getArchitectureTree({
        includeDrafts: isEditMode.value && isStandaloneJobNav.value,
      });

      treeData.value = response;

      return treeData.value;
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job Architecture load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const setEditMode = async (value) => {
    const next = Boolean(value);

    isEditMode.value = next;

    if (!next) {
      clearSelectedProfileUlids();
      closeRowAction();
    }

    // Standalone shows drafts only while editing, so reload on every switch.
    if (isStandaloneJobNav.value) {
      await loadTree();
    }
  };

  const selectFamily = async (family) => {
    selectedFamily.value = family;
    selectedFamilyProfiles.value = [];

    if (!family) {
      return;
    }

    const ulids = getPublishedArchitectureProfiles(family.profiles || [])
      .map((profile) => profile.ulid)
      .filter(Boolean);

    setLoading({ listLoading: true });

    try {
      const profiles = ulids.length > 0 ? await getProfilesByUlids(ulids) : [];

      const normalized = (profiles || []).map((profile) => {
        const customFields = normalizeCustomFields(profile.custom_fields);

        return { ...profile, custom_fields: customFields };
      });

      selectedFamilyProfiles.value = normalized;
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job profiles load error: ${err.message || err}`,
      });

      selectedFamilyProfiles.value = [];
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const clearSelection = () => {
    selectedFamily.value = null;
    selectedFamilyProfiles.value = [];
  };

  const toggleGroupExpanded = (groupNodeId) => {
    const updatedExpandedGroupIds = new Set(expandedGroupIds.value);

    if (updatedExpandedGroupIds.has(groupNodeId)) updatedExpandedGroupIds.delete(groupNodeId);
    else updatedExpandedGroupIds.add(groupNodeId);

    expandedGroupIds.value = updatedExpandedGroupIds;
  };

  const loadJobFamilyOptions = async () => {
    try {
      const [families, groups] = await Promise.all([
        fetchJobFamilies(),
        fetchJobFamilyGroups(),
      ]);

      jobFamilies.value = families?.job_families ?? [];
      jobFamilyGroups.value = groups?.job_family_groups ?? [];
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to load job families — ${err.message || err}`,
      });

      throw err;
    }
  };

  const updateSelectedProfilesFamily = async ({ familyUlid, familyName, ulids: ulidsOverride }) => {
    const fromSelection = ulidsOverride === undefined;
    const ulids = fromSelection
      ? [...selectedProfileUlids.value]
      : [...ulidsOverride];

    if (!ulids.length || !familyUlid) {
      return;
    }

    let cappedUlids = ulids;

    if (cappedUlids.length > BULK_ARCHITECTURE_ACTION_LIMIT) {
      snackbar.addErrorSnackbar({
        text: `You can only move ${BULK_ARCHITECTURE_ACTION_LIMIT} job profiles at once.`,
      });
      cappedUlids = cappedUlids.slice(0, BULK_ARCHITECTURE_ACTION_LIMIT);
    }

    const count = cappedUlids.length;
    const displayName = familyName || 'Job Family';

    editActionLoading.value = true;

    try {
      const { results, count: successCount } = await bulkUpdateProfileFamily({
        ulids: cappedUlids,
        familyUlid,
      });

      const succeeded = countBulkActionSuccesses(results, successCount ?? count);
      const failed = count - succeeded;

      let text = succeeded === 1
        ? `1 Job Profile was added to the ${displayName} Job Family`
        : `${succeeded} Job Profiles were added to the ${displayName} Job Family`;

      if (failed > 0) {
        text = `${text} ${failed} could not be updated.`;
      }

      snackbar.addSuccessSnackbar({ text });

      if (fromSelection) {
        clearSelectedProfileUlids();
      } else {
        closeRowAction();
      }

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to update job family — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const removeSelectedProfilesFromFamily = async ({ ulids: ulidsOverride } = {}) => {
    const fromSelection = ulidsOverride === undefined;
    const ulids = fromSelection
      ? [...selectedProfileUlids.value]
      : [...ulidsOverride];

    if (!ulids.length) {
      return;
    }

    let cappedUlids = ulids;

    if (cappedUlids.length > BULK_ARCHITECTURE_ACTION_LIMIT) {
      snackbar.addErrorSnackbar({
        text: `You can only remove ${BULK_ARCHITECTURE_ACTION_LIMIT} job profiles at once.`,
      });
      cappedUlids = cappedUlids.slice(0, BULK_ARCHITECTURE_ACTION_LIMIT);
    }

    const count = cappedUlids.length;
    const families = getFamiliesForProfiles(treeData.value, cappedUlids);
    const familyCount = families.length;

    editActionLoading.value = true;

    try {
      const { results, count: successCount } = await bulkRemoveProfilesFromFamily({ ulids: cappedUlids });

      const succeeded = countBulkActionSuccesses(results, successCount ?? count);
      const failed = count - succeeded;

      let text;

      if (familyCount === 1) {
        const familyName = families[0].name;

        text = succeeded === 1
          ? `1 Job Profile was removed from the ${familyName} Job Family`
          : `${succeeded} Job Profiles were removed from the ${familyName} Job Family`;
      } else {
        const familiesLabel = familyCount === 0
          ? 'Job Families'
          : `${familyCount} Job Families`;

        text = succeeded === 1
          ? `1 Job Profile was removed from ${familiesLabel}`
          : `${succeeded} Job Profiles were removed from ${familiesLabel}`;
      }

      if (failed > 0) {
        text = `${text} ${failed} could not be updated.`;
      }

      snackbar.addSuccessSnackbar({ text });

      if (fromSelection) {
        clearSelectedProfileUlids();
      } else {
        closeRowAction();
      }

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to remove from job family — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const addGroup = async ({ name }) => {
    editActionLoading.value = true;

    try {
      await createJobFamilyGroup({ name });

      snackbar.addSuccessSnackbar({
        text: `${name} has been added`,
      });

      closeRowAction();

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to add job family group — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const addFamily = async ({ name, groupUlid }) => {
    editActionLoading.value = true;

    try {
      await createJobFamily({ name, groupUlid });

      snackbar.addSuccessSnackbar({
        text: `${name} has been added`,
      });

      closeRowAction();

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to add job family — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const renameGroup = async ({ ulid, oldName, newName }) => {
    editActionLoading.value = true;

    try {
      await updateJobFamilyGroupName({ ulid, name: newName });

      snackbar.addSuccessSnackbar({
        text: `${oldName} has been renamed to ${newName}`,
      });

      closeRowAction();

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to rename job family group — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const renameFamily = async ({ ulid, oldName, newName }) => {
    editActionLoading.value = true;

    try {
      await updateJobFamilyName({ ulid, name: newName });

      snackbar.addSuccessSnackbar({
        text: `${oldName} has been renamed to ${newName}`,
      });

      closeRowAction();

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to rename job family — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const moveFamily = async ({
    familyUlid,
    fromGroupUlid,
    toGroupUlid,
    toGroupName,
  }) => {
    editActionLoading.value = true;

    try {
      await updateJobFamilyGroup({ familyUlid, fromGroupUlid, toGroupUlid });

      snackbar.addSuccessSnackbar({
        text: `Job family and associated job profiles have been moved to ${toGroupName}`,
      });

      closeRowAction();

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to move job family — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const deleteFamily = async ({ ulid, name }) => {
    editActionLoading.value = true;

    try {
      await deleteJobFamily({ ulid });

      snackbar.addSuccessSnackbar({
        text: `${name} has been deleted`,
      });

      closeRowAction();

      await loadTree();
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Failed to delete job family — ${err.message || err}`,
      });

      throw err;
    } finally {
      editActionLoading.value = false;
    }
  };

  const downloadArchitectureCsv = async (tree) => {
    if (!tree?.groups?.length) return;

    xlsxDownloadLoading.value = true;

    const COLORS = {
      header: '#E0E0E0',
      group: '#C9D8E8',
      family: '#E2EBF3',
      profile: '#FFFFFF',
    };

    const BORDER = {
      borderStyle: 'thin',
      borderColor: '#BDBDBD',
    };

    const headerCell = (text) => ({
      value: text,
      fontWeight: 'bold',
      backgroundColor: COLORS.header,
      ...BORDER,
    });

    const boldCell = (value, bg) => ({
      value: value ?? '',
      fontWeight: 'bold',
      backgroundColor: bg,
      ...BORDER,
    });

    const profileCell = (value) => ({
      value: value ?? '',
      backgroundColor: COLORS.profile,
      ...BORDER,
    });

    try {
      // eslint-disable-next-line import/no-extraneous-dependencies
      const { default: writeExcelFile } = await import('write-excel-file/browser');

      // Standalone has no employee data yet, so leave the employee count out.
      const showEmployeeCount = !isStandaloneJobNav.value;

      const header = [
        'Job Family Group', 'Job Family', 'Job Profile', 'Job Code', 'Job Level',
        'Job Family Count', 'Job Profile Count',
        ...(showEmployeeCount ? ['Employee Count'] : []),
        'Alerts',
      ].map((h) => headerCell(h));

      const profileRows = (group, family) => getPublishedArchitectureProfiles(family.profiles).map((profile) => ([
        profileCell(architectureDisplayName(group.name)),
        profileCell(architectureDisplayName(family.name)),
        profileCell(profile.title),
        profileCell(Number(profile.hris_job_code) || profile.hris_job_code || ''),
        profileCell(formatJobLevel(profile.job_level, { mode: 'combined' })),
        profileCell(''),
        profileCell(''),
        ...(showEmployeeCount ? [profileCell(profile.employee_count || 0)] : []),
        profileCell(''),
      ]));

      const familyRows = (group) => (group.families ?? []).flatMap((family) => {
        const { profileCount: famProfiles, employeeCount: famEmployees } = getFamilyArchitectureStats(family);

        return [
          [
            boldCell(architectureDisplayName(group.name), COLORS.family),
            boldCell(architectureDisplayName(family.name), COLORS.family),
            boldCell('', COLORS.family),
            boldCell('', COLORS.family),
            boldCell('', COLORS.family),
            boldCell('', COLORS.family),
            boldCell(famProfiles, COLORS.family),
            ...(showEmployeeCount ? [boldCell(famEmployees, COLORS.family)] : []),
            boldCell('', COLORS.family),
          ],
          ...profileRows(group, family),
        ];
      });

      const dataRows = tree.groups.flatMap((group) => {
        const { familyCount, employeeCount: groupEmployees } = getGroupArchitectureStats(group);
        const profileCount = getGroupTotalProfileCount(group);

        return [
          [
            boldCell(architectureDisplayName(group.name), COLORS.group),
            boldCell('', COLORS.group),
            boldCell('', COLORS.group),
            boldCell('', COLORS.group),
            boldCell('', COLORS.group),
            boldCell(familyCount, COLORS.group),
            boldCell(profileCount, COLORS.group),
            ...(showEmployeeCount ? [boldCell(groupEmployees, COLORS.group)] : []),
            boldCell('', COLORS.group),
          ],
          ...familyRows(group),
        ];
      });

      const allRows = [header, ...dataRows];
      const colCount = header.length;

      const columns = Array.from({ length: colCount }, (_, colIdx) => {
        const maxLen = allRows.reduce((max, row) => {
          const cell = row[colIdx];
          const len = String(cell?.value ?? '').length;

          return Math.max(max, len);
        }, 0);
        return { width: maxLen + 2 };
      });

      const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

      await writeExcelFile([header, ...dataRows], {
        columns,
      }).toFile(`job-architecture-table-${today}.xlsx`);
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job Architecture export error: ${err.message || err}`,
      });
    } finally {
      xlsxDownloadLoading.value = false;
    }
  };

  return {
    treeData,
    selectedFamily,
    selectedFamilyProfiles,
    expandedGroupIds,
    selectedProfileUlids,
    isEditMode,
    editActionLoading,
    jobFamilies,
    jobFamilyGroups,
    rowAction,
    listFilterSnapshot,
    listDefaultExpandedNodeIds,
    loadTree,
    loadJobFamilyOptions,
    updateSelectedProfilesFamily,
    removeSelectedProfilesFromFamily,
    openRowAction,
    closeRowAction,
    addGroup,
    addFamily,
    renameGroup,
    renameFamily,
    moveFamily,
    deleteFamily,
    selectFamily,
    clearSelection,
    toggleGroupExpanded,
    xlsxDownloadLoading,
    downloadArchitectureCsv,
    setSelectedProfileUlids,
    clearSelectedProfileUlids,
    setEditMode,
    resetEditMode,
    setListDefaultExpandedNodeIds,
    clearListDefaultExpandedNodeIds,
    setCompareSelectionUlids,
    saveListFilterSnapshot,
    getAndClearListFilterSnapshot,
    clearListFilterSnapshot,
  };
});
